import { useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UseInstallPromptResult {
  canInstall: boolean;
  isIOS: boolean;
  triggerInstall: () => Promise<void>;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  // Stable values — computed once via refs to avoid effect dep churn
  const isIOS = useRef(/iphone|ipad|ipod/i.test(navigator.userAgent)).current;
  const isStandalone = useRef(
    window.matchMedia('(display-mode: standalone)').matches
  ).current;

  useEffect(() => {
    if (isStandalone) return;

    if (isIOS) {
      setCanInstall(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerInstall = async () => {
    const prompt = deferredPrompt.current;
    if (!prompt) return;

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    // Consume the prompt regardless of outcome — it can't be reused
    deferredPrompt.current = null;

    if (outcome === 'accepted') {
      setCanInstall(false);
    }
  };

  return { canInstall, isIOS, triggerInstall };
}
